import { useState } from 'react'
import { useSpaceConflicts, useResolveSpaceConflict } from '@/hooks/useSpaceConflicts'
import { useTenant } from '@/contexts'
import { useAuth } from '@/contexts'
import { SpaceConflictsTable } from '@/components/space-conflicts/SpaceConflictsTable'
import { ConflictResolutionDialog } from '@/components/space-conflicts/ConflictResolutionDialog'
import type { SpaceConflictTableRow, SpaceConflictFilters, ResolutionType } from '@/types/space-conflicts'
import { PageHeader, PageContainer } from '@/components/layout'

export function SpaceConflictsPage() {
  const { currentOrganization } = useTenant()
  const { user } = useAuth()
  const [filters] = useState<SpaceConflictFilters>({
    status: ['open'], // Default to show open conflicts
  })
  const [selectedConflict, setSelectedConflict] = useState<SpaceConflictTableRow | null>(null)
  const [isResolutionDialogOpen, setIsResolutionDialogOpen] = useState(false)

  // Queries and mutations
  const { data: conflicts = [], isLoading: isLoadingConflicts } = useSpaceConflicts(filters)
  const resolveConflict = useResolveSpaceConflict()

  // Handle resolution submission
  const handleResolveSubmit = async (
    resolutionType: ResolutionType | null,
    resolutionNotes: string
  ) => {
    if (!selectedConflict || !user?.id) return

    try {
      await resolveConflict.mutateAsync({
        id: selectedConflict.id,
        data: {
          status: 'resolved',
          resolution_type: resolutionType,
          resolution_notes: resolutionNotes || null,
          resolved_at: new Date().toISOString(),
          resolved_by: user.id,
        },
      })
      setIsResolutionDialogOpen(false)
      setSelectedConflict(null)
    } catch (error) {
      // Error is handled by the mutation's onError callback
      console.error('Failed to resolve conflict:', error)
    }
  }

  // Handle open resolution dialog
  const handleOpenResolution = (conflictId: string, _unitCode: string | null) => {
    const conflict = conflicts.find((c) => c.id === conflictId)
    if (conflict) {
      setSelectedConflict(conflict)
      setIsResolutionDialogOpen(true)
    }
  }

  // Calculate summary stats
  const openConflicts = conflicts.filter((c) => c.status === 'open').length
  const criticalConflicts = conflicts.filter(
    (c) => c.status === 'open' && c.severity === 'critical'
  ).length
  const totalAffected = conflicts.reduce(
    (sum, c) => sum + (c.affected_transient_ids?.length ?? 0),
    0
  )

  return (
    <PageContainer>
      <PageHeader
        title="Space Conflicts & Alerts"
        description="Monitor and resolve conflicts in aircraft placement and scheduling"
      />

      {/* Summary Stats */}
      <div className="grid gap-4 md:grid-cols-3 mb-6">
        <div className="rounded-lg border bg-card p-4">
          <div className="text-sm font-medium text-muted-foreground">Open Conflicts</div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-3xl font-bold">{openConflicts}</span>
            {criticalConflicts > 0 && (
              <span className="text-xs font-medium text-destructive">
                {criticalConflicts} critical
              </span>
            )}
          </div>
        </div>

        <div className="rounded-lg border bg-card p-4">
          <div className="text-sm font-medium text-muted-foreground">Total Affected Transients</div>
          <div className="mt-2">
            <span className="text-3xl font-bold">{totalAffected}</span>
          </div>
        </div>

        <div className="rounded-lg border bg-card p-4">
          <div className="text-sm font-medium text-muted-foreground">Organization</div>
          <div className="mt-2">
            <span className="text-lg font-medium truncate">{currentOrganization?.name}</span>
          </div>
        </div>
      </div>

      {/* Conflicts Table */}
      <div className="rounded-lg border bg-card">
        <div className="p-6">
          <SpaceConflictsTable
            data={conflicts}
            isLoading={isLoadingConflicts}
            onResolve={handleOpenResolution}
          />
        </div>
      </div>

      {/* Resolution Dialog */}
      <ConflictResolutionDialog
        conflict={selectedConflict}
        isOpen={isResolutionDialogOpen}
        isSubmitting={resolveConflict.isPending}
        onClose={() => {
          setIsResolutionDialogOpen(false)
          setSelectedConflict(null)
        }}
        onSubmit={handleResolveSubmit}
      />
    </PageContainer>
  )
}
